import { useEffect, useRef, useState } from "react";
import axios from "axios";
import "@/App.css";
import EverAfterCabinet from "@/components/EverAfterCabinet";
import AdminApp from "@/AdminApp";
import SovaLanding from "@/SovaLanding";
import SharedPlanView from "@/SharedPlanView";
import ExpertApplicationView from "@/ExpertApplicationView";
import BenchBookingSuccessView from "@/BenchBookingSuccessView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Always send the session_token cookie with API calls.
axios.defaults.withCredentials = true;

// HEY SOVA — brutalist B/W brand tokens.
// Variable names are kept for backwards-compat with existing imports across
// EverAfterCabinet.jsx, AdminApp.js, etc. The semantic meaning has shifted:
//   NAVY  → primary ink (black)
//   ROSE  → muted secondary (mid-gray) used for eyebrows/accents
//   CREAM → page background (white)
//   PAPER → card background (white)
//   HAIR  → hard 2px border ink (solid black)
const NAVY = "#000000";
const ROSE = "#666666";
const CREAM = "#FFFFFF";
const PAPER = "#FFFFFF";
const HAIR = "#000000";
const DISPLAY = "'Archivo Black', 'Inter', system-ui, sans-serif";
const BODY = "'Inter', system-ui, -apple-system, sans-serif";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
    redirectUrl
  )}`;
}

// Send any locally-stored acknowledgement timestamps to the server. Idempotent.
async function recordAcknowledgements(user) {
  if (!user || user.acknowledgements) return; // already on file
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem("cabinet_terms") || "{}");
  } catch {
    stored = {};
  }
  const fallback = stored.accepted_at || new Date().toISOString();
  try {
    await axios.post(`${API}/auth/accept-terms`, {
      terms_accepted_at: stored.terms ? fallback : null,
      privacy_accepted_at: stored.privacy ? fallback : null,
      ai_accepted_at: stored.ai ? fallback : null,
    });
  } catch (e) {
    // non-fatal — user can still use the app, will retry on next login
    console.warn("recordAcknowledgements failed (non-fatal)", e);
  }
}

function AuthCallback({ onAuthed }) {
  const ref = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (ref.current) return;
    ref.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setError("Missing session id.");
      return;
    }
    const sessionId = decodeURIComponent(m[1]);
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        // Persist acknowledgement timestamps to the user record (legal audit trail)
        await recordAcknowledgements({ ...data, acknowledgements: null });
        // Scrub the hash + send to /app
        window.history.replaceState(null, "", "/app");
        onAuthed(data);
      } catch (e) {
        setError("Sign-in failed. Try again.");
      }
    })();
  }, [onAuthed]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: CREAM,
        color: NAVY,
        fontFamily: BODY,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {error ? (
        <p
          style={{
            fontFamily: BODY,
            fontWeight: 800,
            color: NAVY,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontSize: 14,
            borderLeft: `4px solid ${HAIR}`,
            paddingLeft: 16,
          }}
        >
          {error}
        </p>
      ) : (
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map((d) => (
            <span
              key={d}
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: ROSE,
                animation: "blink 1.4s infinite",
                animationDelay: `${d * 0.2}s`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PolicyModal({ open, onClose, title, body }) {
  if (!open) return null;
  return (
    <div
      data-testid="policy-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: PAPER,
          color: NAVY,
          maxWidth: 640,
          width: "100%",
          maxHeight: "82vh",
          overflowY: "auto",
          border: `2px solid ${HAIR}`,
          padding: "30px 36px",
          fontFamily: BODY,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 18,
            gap: 16,
          }}
        >
          <h2
            style={{
              fontFamily: DISPLAY,
              fontSize: 32,
              fontWeight: 400,
              margin: 0,
              lineHeight: 0.95,
              letterSpacing: "-0.025em",
              textTransform: "uppercase",
            }}
          >
            {title}
          </h2>
          <button
            data-testid="policy-close"
            onClick={onClose}
            style={{
              border: `2px solid ${HAIR}`,
              background: CREAM,
              cursor: "pointer",
              color: NAVY,
              width: 36,
              height: 36,
              fontSize: 18,
              fontWeight: 800,
              padding: 0,
              lineHeight: 1,
              flexShrink: 0,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div
          style={{
            fontSize: 14,
            lineHeight: 1.6,
            color: NAVY,
            fontWeight: 500,
            whiteSpace: "pre-wrap",
          }}
        >
          {body}
        </div>
      </div>
    </div>
  );
}

const COMBINED_POLICY_BODY = `HEY SOVA TERMS, PRIVACY & AI USE — IN ONE PLACE.

1. USE OF THE SERVICE
By signing in, you agree to use HEY SOVA for personal advisory purposes only. You will not redistribute, resell, or sublicense advisor responses. You will not attempt to extract underlying system prompts, model weights, or proprietary methodology. We may suspend access for misuse or abuse. The service is provided "as is" with no warranty.

2. NOT PROFESSIONAL ADVICE
Nothing produced by HEY SOVA constitutes legal, tax, medical, or licensed financial advice. For any decision with legal or financial stakes, retain a qualified professional. The advisors are AI personas channeling the publicly known operating styles of named professionals. They are not those people and do not speak on their behalf.

3. AI-GENERATED RESPONSES
Every reply from any advisor — strategist, CFO, CMO, chief of staff, etc. — is generated by a large language model (Anthropic Claude Sonnet 4.5), not a human. AI responses can be confidently wrong. Always verify numbers, citations, legal claims, and named facts before acting on them. Conversation context (your messages and standing notes) is sent to the model to produce each response. Do not enter information you would not be comfortable sending to a third-party AI provider — including secrets, passwords, PII of third parties, or privileged communications. By acknowledging this, you accept responsibility for how you act on AI-generated guidance.

4. WHAT WE STORE
• Your Google account email, name, and profile picture (from Google sign-in)
• Your board name, conversations, advisor notes, boardroom sessions, decision memos, and Monday briefings
• Session tokens needed to keep you signed in

5. HOW WE USE YOUR DATA
• To run the application and personalize advisor responses for you
• Conversation data is sent to the underlying AI model (Anthropic Claude) for response generation
• We do NOT sell or rent your personal data, and we do NOT share it with advertisers

6. YOUR RIGHTS
• Sign out at any time
• On request, your account and all associated data can be permanently deleted
• Authentication uses Google OAuth and a httpOnly secure session cookie
• Data is stored in a managed database and not publicly exposed

By checking the box below, you confirm you have read and accept all of the above.`;

function LoginView() {
  const stored = (() => {
    try {
      return JSON.parse(localStorage.getItem("cabinet_terms") || "{}");
    } catch {
      return {};
    }
  })();
  // Single combined acknowledgment — covers Terms, Privacy, and AI use.
  const [ack, setAck] = useState(
    Boolean(stored.terms && stored.privacy && stored.ai)
  );
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    localStorage.setItem(
      "cabinet_terms",
      JSON.stringify({
        terms: ack,
        privacy: ack,
        ai: ack,
        accepted_at: ack ? new Date().toISOString() : null,
      })
    );
  }, [ack]);

  const ready = ack;

  return (
    <div
      data-testid="login-view"
      style={{
        minHeight: "100vh",
        background: CREAM,
        color: NAVY,
        fontFamily: BODY,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 22px",
      }}
    >
      <div style={{ maxWidth: 520, width: "100%" }}>
        <div
          style={{
            fontFamily: BODY,
            fontSize: 11,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            fontWeight: 800,
            color: NAVY,
            marginBottom: 14,
          }}
        >
          Hey Sova
        </div>
        <h1
          style={{
            fontFamily: DISPLAY,
            fontSize: 56,
            margin: "0 0 18px",
            fontWeight: 400,
            lineHeight: 0.92,
            letterSpacing: "-0.03em",
            textTransform: "uppercase",
          }}
        >
          Sign in to your<br />executive.
        </h1>
        <p
          style={{
            fontFamily: BODY,
            fontSize: 16,
            color: NAVY,
            margin: "0 0 32px",
            lineHeight: 1.5,
            fontWeight: 600,
            maxWidth: 440,
          }}
        >
          Sixteen advisors, briefed and waiting. Sign in with Google to convene your room.
        </p>

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            textAlign: "left",
            fontSize: 14,
            color: NAVY,
            lineHeight: 1.5,
            cursor: "pointer",
            border: `2px solid ${HAIR}`,
            padding: "20px 22px",
            background: PAPER,
            marginBottom: 22,
            fontWeight: 600,
          }}
        >
          <input
            type="checkbox"
            data-testid="agree-terms"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            style={{
              marginTop: 3,
              width: 18,
              height: 18,
              accentColor: NAVY,
              flexShrink: 0,
              cursor: "pointer",
            }}
          />
          <span>
            I agree to the Terms, Privacy Policy, and acknowledge that all advisor responses
            are AI-generated.{" "}
            <button
              type="button"
              data-testid="open-terms"
              onClick={(e) => {
                e.preventDefault();
                setOpenModal(true);
              }}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                color: NAVY,
                fontFamily: BODY,
                fontSize: 14,
                fontWeight: 800,
                textDecoration: "underline",
                textUnderlineOffset: 3,
                cursor: "pointer",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Read full terms →
            </button>
          </span>
        </label>

        <button
          data-testid="login-google-btn"
          onClick={() => ready && loginWithGoogle()}
          disabled={!ready}
          style={{
            width: "100%",
            border: `2px solid ${HAIR}`,
            cursor: ready ? "pointer" : "not-allowed",
            background: ready ? NAVY : "#CCCCCC",
            color: CREAM,
            padding: "18px 22px",
            fontFamily: BODY,
            fontSize: 15,
            fontWeight: 800,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            transition: "transform .15s ease, box-shadow .15s ease",
          }}
          onMouseEnter={(e) => {
            if (!ready) return;
            e.currentTarget.style.transform = "translate(-3px,-3px)";
            e.currentTarget.style.boxShadow = `6px 6px 0 ${HAIR}`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          Continue with Google →
        </button>
        <p
          style={{
            marginTop: 22,
            fontSize: 12,
            color: ROSE,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Your conversations, notes & boardrooms stay private to your account.
        </p>
      </div>

      <PolicyModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title="Terms · Privacy · AI Use"
        body={COMBINED_POLICY_BODY}
      />
    </div>
  );
}

function ReacceptanceGate({ user, onAccepted, onLogout }) {
  const [ack, setAck] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const ready = ack;

  async function accept() {
    if (!ready || saving) return;
    setSaving(true);
    const now = new Date().toISOString();
    try {
      await axios.post(`${API}/auth/accept-terms`, {
        terms_accepted_at: now,
        privacy_accepted_at: now,
        ai_accepted_at: now,
      });
      localStorage.setItem(
        "cabinet_terms",
        JSON.stringify({ terms: true, privacy: true, ai: true, accepted_at: now })
      );
      onAccepted();
    } catch (e) {
      console.error("accept-terms failed", e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="reacceptance-gate"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <div
        style={{
          background: PAPER,
          color: NAVY,
          maxWidth: 560,
          width: "100%",
          border: `2px solid ${HAIR}`,
          padding: "32px 36px",
          fontFamily: BODY,
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: NAVY,
            fontWeight: 800,
            marginBottom: 10,
          }}
        >
          Policy update
        </div>
        <h2
          style={{
            fontFamily: DISPLAY,
            fontSize: 36,
            fontWeight: 400,
            margin: "0 0 14px",
            lineHeight: 0.95,
            letterSpacing: "-0.025em",
            textTransform: "uppercase",
          }}
        >
          We've updated<br />our policies.
        </h2>
        <p
          style={{
            fontFamily: BODY,
            fontSize: 15,
            color: NAVY,
            margin: "0 0 22px",
            lineHeight: 1.5,
            fontWeight: 600,
          }}
        >
          Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. Take a moment to review
          and re-acknowledge before you continue.
        </p>

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            textAlign: "left",
            fontSize: 14,
            color: NAVY,
            lineHeight: 1.5,
            cursor: "pointer",
            border: `2px solid ${HAIR}`,
            padding: "18px 20px",
            background: CREAM,
            marginBottom: 22,
            fontWeight: 600,
          }}
        >
          <input
            type="checkbox"
            data-testid="reaccept-terms"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            style={{
              marginTop: 3,
              width: 18,
              height: 18,
              accentColor: NAVY,
              flexShrink: 0,
              cursor: "pointer",
            }}
          />
          <span>
            I agree to the updated Terms, Privacy Policy, and acknowledge that advisor responses
            are AI-generated.{" "}
            <button
              type="button"
              data-testid="reaccept-open-terms"
              onClick={(e) => {
                e.preventDefault();
                setOpenModal(true);
              }}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                color: NAVY,
                fontFamily: BODY,
                fontSize: 14,
                fontWeight: 800,
                textDecoration: "underline",
                textUnderlineOffset: 3,
                cursor: "pointer",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              Read full terms →
            </button>
          </span>
        </label>

        <div style={{ display: "flex", gap: 12 }}>
          <button
            data-testid="reaccept-logout"
            onClick={onLogout}
            style={{
              flex: 1,
              border: `2px solid ${HAIR}`,
              background: CREAM,
              cursor: "pointer",
              color: NAVY,
              padding: "14px 16px",
              fontFamily: BODY,
              fontSize: 14,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Sign out
          </button>
          <button
            data-testid="reaccept-submit"
            onClick={accept}
            disabled={!ready || saving}
            style={{
              flex: 2,
              border: `2px solid ${HAIR}`,
              cursor: ready && !saving ? "pointer" : "not-allowed",
              background: ready && !saving ? NAVY : "#CCCCCC",
              color: CREAM,
              padding: "14px 18px",
              fontFamily: BODY,
              fontSize: 14,
              fontWeight: 800,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {saving ? "Saving…" : "I acknowledge — continue"}
          </button>
        </div>
      </div>

      <PolicyModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title="Terms · Privacy · AI Use"
        body={COMBINED_POLICY_BODY}
      />
    </div>
  );
}

function Paywall({ user, onLogout, onPaid }) {
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState("");

  // Detect return from Stripe via ?session_id=...
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const sid = sp.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          // Clean URL and refresh user
          window.history.replaceState(null, "", "/app");
          onPaid();
          return;
        }
        if (data.status === "expired" || attempts >= 8) {
          setError("Payment didn't complete. Try again.");
          setPolling(false);
          return;
        }
        setTimeout(poll, 2500);
      } catch {
        setError("Couldn't verify payment. Refresh and try again.");
        setPolling(false);
      }
    };
    poll();
  }, [onPaid]);

  async function subscribe() {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/checkout/start`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      console.error("subscribe checkout failed", e);
      setError("Couldn't start checkout. Try again.");
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="paywall-view"
      style={{
        minHeight: "100vh",
        background: CREAM,
        color: NAVY,
        fontFamily: BODY,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 22px",
      }}
    >
      <div style={{ maxWidth: 520, width: "100%" }}>
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: NAVY,
            fontWeight: 800,
            marginBottom: 14,
          }}
        >
          Beta access
        </div>
        <h1
          style={{
            fontFamily: DISPLAY,
            fontSize: 56,
            margin: "0 0 18px",
            fontWeight: 400,
            lineHeight: 0.92,
            letterSpacing: "-0.03em",
            textTransform: "uppercase",
          }}
        >
          Your trial<br />has ended.
        </h1>
        <p
          style={{
            fontFamily: BODY,
            fontSize: 16,
            color: NAVY,
            margin: "0 0 26px",
            lineHeight: 1.5,
            fontWeight: 600,
          }}
        >
          Continue with your sixteen advisors for $80/month while we're in beta. Auto-renews monthly. Cancel anytime.
        </p>

        <div
          style={{
            border: `2px solid ${HAIR}`,
            background: PAPER,
            padding: "26px 28px",
            marginBottom: 22,
            textAlign: "left",
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: NAVY,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.18em",
            }}
          >
            Beta · monthly
          </div>
          <div
            style={{
              fontFamily: DISPLAY,
              fontSize: 72,
              fontWeight: 400,
              marginTop: 8,
              lineHeight: 0.9,
              letterSpacing: "-0.04em",
            }}
          >
            ${user?.subscription?.monthly_price_usd || 80}
            <span
              style={{
                fontFamily: BODY,
                fontSize: 14,
                color: NAVY,
                fontWeight: 700,
                marginLeft: 8,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              / month
            </span>
          </div>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "22px 0 0",
              fontSize: 14,
              lineHeight: 1.5,
              color: NAVY,
              fontWeight: 600,
              display: "grid",
              gap: 12,
            }}
          >
            {[
              "Unlimited 1:1 conversations with all sixteen advisors",
              "Convene the boardroom + AI decision memos",
              "Monday operating brief by your chief of staff",
              "Action plan with AI milestone extraction",
              "Card on file · auto-renews monthly · cancel anytime",
            ].map((line) => (
              <li
                key={line}
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    width: 14,
                    height: 14,
                    background: NAVY,
                    marginTop: 4,
                  }}
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        {error && (
          <p
            style={{
              color: NAVY,
              fontSize: 13,
              marginBottom: 12,
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              borderLeft: `4px solid ${HAIR}`,
              paddingLeft: 12,
            }}
          >
            {error}
          </p>
        )}

        <button
          data-testid="subscribe-btn"
          onClick={subscribe}
          disabled={busy || polling}
          style={{
            width: "100%",
            border: `2px solid ${HAIR}`,
            cursor: busy || polling ? "default" : "pointer",
            background: busy || polling ? "#CCCCCC" : NAVY,
            color: CREAM,
            padding: "18px 22px",
            fontFamily: BODY,
            fontSize: 15,
            fontWeight: 800,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            transition: "transform .15s ease, box-shadow .15s ease",
          }}
          onMouseEnter={(e) => {
            if (busy || polling) return;
            e.currentTarget.style.transform = "translate(-3px,-3px)";
            e.currentTarget.style.boxShadow = `6px 6px 0 ${HAIR}`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          {polling ? "Verifying payment…" : busy ? "Opening checkout…" : "Subscribe — $80/mo · auto-renews"}
        </button>

        <button
          data-testid="paywall-logout"
          onClick={onLogout}
          style={{
            marginTop: 18,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: ROSE,
            fontSize: 12,
            fontFamily: BODY,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function App() {
  // null = checking, true = authed, false = not authed
  const [authed, setAuthed] = useState(null);
  const [user, setUser] = useState(null);
  const hasSessionHash =
    typeof window !== "undefined" && window.location.hash.includes("session_id=");
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const isAdminPath = path.startsWith("/admin");
  const isAppPath = path === "/app" || path.startsWith("/app/");
  const isSharePath = path.startsWith("/share/");
  const shareToken = isSharePath ? path.slice("/share/".length).split("/")[0] : null;
  const isApplyPath = path === "/apply-to-bench" || path === "/apply-to-bench/";
  const isBookedPath = path.startsWith("/bench/booked/");
  const bookingId = isBookedPath ? path.slice("/bench/booked/".length).split("/")[0] : null;
  // Anything else (including "/") is the public Sova landing page
  const isLandingPath = !isAdminPath && !isAppPath && !isSharePath && !isApplyPath && !isBookedPath && !hasSessionHash;

  useEffect(() => {
    if (isAdminPath) return; // /admin is handled by AdminApp; no user auth check
    if (isSharePath) return; // /share/:token is public; no user auth check
    if (isApplyPath) return; // /apply-to-bench is public; no user auth check
    if (isBookedPath) return; // /bench/booked/:id is public; no user auth check
    if (isLandingPath) return; // public landing — no auth check
    if (hasSessionHash) return; // AuthCallback handles it
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        setUser(data);
        setAuthed(true);
      } catch (e) {
        if (!e.response || e.response.status !== 401) {
          console.error("auth/me failed", e);
        }
        setAuthed(false);
      }
    })();
  }, [hasSessionHash, isAdminPath, isLandingPath, isSharePath, isApplyPath, isBookedPath]);

  async function logout() {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.warn("logout failed (non-fatal)", e);
    }
    setUser(null);
    setAuthed(false);
    // Bring the user back to the public landing
    window.location.href = "/";
  }

  if (isAdminPath) return <AdminApp />;

  if (isSharePath && shareToken) return <SharedPlanView token={shareToken} />;

  if (isApplyPath) return <ExpertApplicationView />;

  if (isBookedPath && bookingId) return <BenchBookingSuccessView bookingId={bookingId} />;

  if (isLandingPath) return <SovaLanding />;

  if (hasSessionHash) {
    return (
      <AuthCallback
        onAuthed={(u) => {
          setUser(u);
          setAuthed(true);
        }}
      />
    );
  }

  if (authed === null) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: CREAM,
          color: NAVY,
          fontFamily: BODY,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map((d) => (
            <span
              key={d}
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: ROSE,
                animation: "blink 1.4s infinite",
                animationDelay: `${d * 0.2}s`,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!authed) return <LoginView />;

  // Backend says this user's acknowledgements are out of date (policy_version mismatch)
  if (user && user.requires_reacceptance) {
    return (
      <>
        <EverAfterCabinet user={user} onLogout={logout} />
        <ReacceptanceGate
          user={user}
          onLogout={logout}
          onAccepted={async () => {
            try {
              const { data } = await axios.get(`${API}/auth/me`);
              setUser(data);
            } catch (e) {
              console.warn("refresh user after accept-terms failed", e);
            }
          }}
        />
      </>
    );
  }

  // Hard paywall: trial ended and no active subscription
  if (user && user.subscription && user.subscription.requires_payment) {
    return (
      <Paywall
        user={user}
        onLogout={logout}
        onPaid={async () => {
          try {
            const { data } = await axios.get(`${API}/auth/me`);
            setUser(data);
          } catch (e) {
            console.warn("refresh user after paid failed", e);
          }
        }}
      />
    );
  }

  return <EverAfterCabinet user={user} onLogout={logout} />;
}

// Shared constants for child views that import them
export { NAVY, ROSE, CREAM, PAPER, HAIR, DISPLAY, BODY };
